import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DownloadIcon from "@mui/icons-material/Download";
import CustomButton from "../../../component/elements/CustomButton/CustomButton";
import axiosInstance from "../../../configuration/request";
import { useSnackbar } from "../../../component/SnackbarContext";
import { useSelector } from "react-redux";
import FeedbackCard from "./feedbackCard";
import { dateFormat, formatDateToDDMMYYYY } from "../../../utils/dateFormat";

const ratings = ["All", "5 Stars", "4 Stars", "3 Stars", "2 Stars", "1 Star"];

const FeedbackManagement = () => {
  const showSnackbar = useSnackbar();
  const { loginDetails } = useSelector((state) => state.counter);
  const storeId = loginDetails.storeId || null;

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedRating, setSelectedRating] = useState("All");
  const [replyingIndex, setReplyingIndex] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [feedbackData, setFeedbackData] = useState([]);
  const [avgRating, setAvgRating] = useState([]);
  const [page, setPage] = useState(1);    
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchFeedback(1);
    setPage(1);
  }, []);

  const fetchFeedback = (pageNumber = page, limit = rowsPerPage) => {
      console.log(pageNumber);
    const params = {
      storeId,
      page: pageNumber,  
      limit
    };
    if (fromDate) params.fromDate = formatDateToDDMMYYYY(fromDate);
    if (toDate) params.toDate = formatDateToDDMMYYYY(toDate);
    if (selectedRating && selectedRating !== "All") {
      params.score = parseInt(selectedRating);
    }

    axiosInstance
      .get(`/customer/store/feedback`, { params })
      .then((res) => {
        const formattedUser = res?.data?.feedbacks?.map((item) => ({
          id: item?.id || "",
          name: item?.customer?.name || "",
          date: dateFormat(item?.createdAt) || "",
          rating: item?.score || "",
          comment: item?.content || "",
          replies: item?.messages?.map((rep) => ({
            name: rep?.sender?.name || "",
            date: dateFormat(rep?.sentAt) || "",
            text: rep?.content || "",
          })),
        }));
        setAvgRating(res?.data?.averageScore || 0);
        setFeedbackData(formattedUser);
        setPage(res?.data?.currentPage || 1);  // adjust to 0-based
        setTotalCount(res?.data?.total || 0);
      })
      .catch((error) => {
          if (error?.response?.data?.message) {
            showSnackbar(error?.response?.data?.message, "error");
          } else {
            showSnackbar("Something went wrong!", "error");
          }
        });
  };

  const handleReply = (id) => {
    const payload = { content: replyText?.trim(),"inAppropriate": false };
    axiosInstance
      .post(`/customer/store/feedback/${id}/create-message`, payload)
      .then(() => {
        showSnackbar("Reply sent successfully!", "success");
        setReplyText("");
        fetchFeedback(page);   // refetch current page
        setReplyingIndex(null);
      })
     .catch((error) => {
          if (error?.response?.data?.message) {
            showSnackbar(error?.response?.data?.message, "error");
          } else {
            showSnackbar("Something went wrong!", "error");
          }
        });
  };

  const handleNextPage = () => {
    const newPage = page + 1;
    if (newPage * rowsPerPage < totalCount) {
      setPage(newPage);
      fetchFeedback(newPage);
    }
  };

  const handlePrevPage = () => {
    const newPage = Math.max(0, page - 1);
    setPage(newPage);
    fetchFeedback(newPage);
  };
 
const handleDownload = () => {
  const params = {
    export: true,
    storeId
  };
  if (fromDate) params.fromDate = formatDateToDDMMYYYY(fromDate);
  if (toDate) params.toDate = formatDateToDDMMYYYY(toDate);
  if (selectedRating && selectedRating !== "All") {
    params.score = parseInt(selectedRating);
  }

  axiosInstance
    .get(`/customer/store/feedback/`, {
      params,
      responseType: "blob",
    })
    .then((response) => {
      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      let filename = "feedback.xlsx";
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    })
   .catch((error) => {
          if (error?.response?.data?.message) {
            showSnackbar(error?.response?.data?.message, "error");
          } else {
            showSnackbar("Something went wrong!", "error");
          }
        });
};

return (
  <div className="flex flex-col lg:flex-row gap-6 py-6 px-4 lg:px-6 overflow-auto">
    {/* Sidebar */}
    <div className="flex flex-col lg:w-[260px] w-full gap-6">
      <div className="space-y-3">
        <Typography fontWeight={700}>Filters</Typography>
        <div className="flex flex-wrap gap-2">
          {ratings.map((rating) => (
            <CustomButton
              key={rating}
              onClick={() => setSelectedRating(rating)}
              customeClass={`w-[80px] h-[30px] ${
                selectedRating === rating ? "gradient-button" : "primary-custom-button"
              }`}
            >
              {rating}
            </CustomButton>
          ))}
        </div>
      </div>

      <div>
        <Typography fontWeight={700}>Date Range</Typography>
        <Box className="space-y-3 mt-2">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From"
              value={fromDate}
              onChange={(date) => setFromDate(date)}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
            <DatePicker
              label="To"
              value={toDate}
              onChange={(date) => setToDate(date)}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </LocalizationProvider>
          <CustomButton
            onClick={() => {console.log(page);fetchFeedback(page)}}
            customeClass="gradient-button w-full h-[40px]"
          >
            Apply Filters
          </CustomButton>
        </Box>
      </div>
    </div>

    {/* Main Content */}
    <div className="flex-1 space-y-6">
      <div className="bg-[#eaf4fa] p-4 rounded-md flex justify-between items-center">
        <div className="font-[600]">
          <div>Average Rating</div>
          <div className="text-xl">{avgRating} / 5</div>
        </div>
        <div className="cursor-pointer" onClick={handleDownload}>
          <IconButton>
            <DownloadIcon />
          </IconButton>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-y-auto hide-scrollbar min-h-[200px] max-h-[70vh] bg-[#eaf4fa] p-3 rounded-md">
        {feedbackData.length > 0 ? (
          feedbackData.map((feedback, idx) => (
            <div key={idx} className="w-full flex">
              <FeedbackCard
                feedback={feedback}
                idx={idx}
                handleReplySubmit={handleReply}
                replyingIndex={replyingIndex}
                setReplyingIndex={setReplyingIndex}
                replyText={replyText}
                setReplyText={setReplyText}
              />
            </div>
          ))
        ) : (
          <div className="flex justify-center items-center h-[190px] text-center text-gray-600 text-base font-medium w-full">
            No feedback available at the moment.
          </div>
        )}
      </div>

      {/* Pagination buttons below cards */}
      <div className="flex justify-end gap-2 mt-2">
        <CustomButton
          onClick={handlePrevPage}
          customeClass="primary-custom-button w-[70px] h-[30px]"
          disabled={page === 1}
        >
          Prev
        </CustomButton>
        <CustomButton
          onClick={handleNextPage}
          customeClass="primary-custom-button w-[70px] h-[30px]"
          disabled={(page * rowsPerPage) >= totalCount}
        >
          Next
        </CustomButton>
      </div>
      {/* <div className="text-xs text-gray-500 text-right">
        Page {page + 1} of {Math.ceil(totalCount / rowsPerPage)}
      </div> */}
    </div>
  </div>
);

};

export default FeedbackManagement;
